/**
 * Utilitários para manipulação de arquivos
 */

/**
 * Converte um objeto File para Blob com tratamento de erro adequado
 * @param file - O arquivo a ser convertido
 * @returns Blob do arquivo
 * @throws Error com mensagem amigável se o arquivo não estiver acessível
 */
export const fileToBlob = async (file: File): Promise<Blob> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    return new Blob([new Uint8Array(arrayBuffer)], {
      type: file.type
    });
  } catch (error) {
    // Este erro ocorre quando o arquivo não está mais acessível
    // (foi movido, renomeado ou excluído do sistema do usuário)
    const errorMessage = (error as Error)?.message || '';

    if (
      errorMessage.includes('could not be found') ||
      errorMessage.includes('NotFoundError') ||
      errorMessage.includes('NotReadableError') ||
      errorMessage.includes('A requested file or directory')
    ) {
      throw new Error(
        `O arquivo "${file.name}" não está mais acessível. Por favor, remova-o e adicione novamente.`
      );
    }

    throw new Error(
      `Erro ao ler o arquivo "${file.name}": ${errorMessage || 'Erro desconhecido'}`
    );
  }
};

/**
 * Sanitiza o nome do arquivo removendo caracteres especiais
 * @param filename - Nome do arquivo original
 * @returns Nome do arquivo sanitizado
 */
export const sanitizeFilename = (filename: string): string => {
  return filename
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[`~!@#$%^&*()_|+\-=?;:'",<>{}[\]\\/]/gi, '');
};

/**
 * Verifica se um arquivo está acessível antes de tentar fazer upload
 * Esta função deve ser chamada ANTES de obter o presigned URL
 * para evitar criar entradas no banco sem arquivo correspondente no S3
 * @param file - O arquivo a ser verificado
 * @returns true se o arquivo está acessível
 * @throws Error com mensagem amigável se o arquivo não estiver acessível
 */
export const validateFileAccessibility = async (file: File): Promise<boolean> => {
  try {
    // Tenta ler um pequeno slice do arquivo para verificar se está acessível
    // Isso é mais eficiente do que ler o arquivo inteiro
    const slice = file.slice(0, 1024); // Lê apenas 1KB
    await slice.arrayBuffer();
    return true;
  } catch (error) {
    const errorMessage = (error as Error)?.message || '';

    if (
      errorMessage.includes('could not be found') ||
      errorMessage.includes('NotFoundError') ||
      errorMessage.includes('NotReadableError') ||
      errorMessage.includes('A requested file or directory')
    ) {
      throw new Error(
        `O arquivo "${file.name}" não está mais acessível. Por favor, remova-o e adicione novamente.`
      );
    }

    throw new Error(
      `Erro ao verificar o arquivo "${file.name}": ${errorMessage || 'Erro desconhecido'}`
    );
  }
};

/**
 * Resultado do upload de um arquivo com informações para rollback
 */
export type FileUploadResult = {
  success: boolean;
  fileId?: string;
  fileName: string;
  error?: string;
};

